import {
    type CSSProperties,
    type ChangeEvent,
    type FC,
    type Ref,
    memo,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import { css, cx } from "@emotion/css";
import { Spinner, Tooltip, usePrevious } from "@snapchat/snap-design-system";
import { SearchIcon } from "@snapchat/snap-design-system-icons";

import { searchBarStyle, searchInputStyle, searchInputWrapperStyle } from "./styles";

export interface BaseComponentProps<RefType = unknown> {
    className?: string;
    id?: string;
    style?: CSSProperties;
    "data-testid"?: string;
    ref?: Ref<RefType>;
}

export interface SearchBarProps extends BaseComponentProps {
    onChange?: (text: string) => unknown;
    placeholder?: string;
    value?: string;
    disabled?: boolean;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
    isLoading?: boolean;
    tooltip?: string;
}

// SDS version https://jira.sc-corp.net/browse/CAMKIT-1629
// Won't use unless SDS Searchbar allows for customizing the disabled field
const SearchBar: FC<SearchBarProps> = memo((props) => {
    const {
        onChange,
        value,
        placeholder,
        "data-testid": dataTestId,
        className,
        style,
        disabled,
        onClick,
        onKeyDown,
        isLoading,
        tooltip,
    } = props;

    const searchBarClass = cx(css(searchBarStyle), "sds-search-bar", className);
    const prevIsLoading = usePrevious(isLoading);
    const [isFocused, setIsFocused] = useState<boolean>();
    const inputRef = useRef<HTMLInputElement | null>();

    const onInputChange = useCallback(
        (evt: ChangeEvent<HTMLInputElement>) => {
            onChange?.(evt.target.value);
        },
        [onChange],
    );

    const onFocus = useCallback(() => {
        setIsFocused(true);
    }, []);

    const onBlur = useCallback(() => {
        // don't blur if data just finished loading
        if (!(prevIsLoading && !isLoading)) {
            setIsFocused(false);
        }
    }, [isLoading, prevIsLoading]);

    useEffect(() => {
        if (isFocused && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isFocused, isLoading]);

    return (
        <div
            className={searchBarClass}
            style={style}
            onClick={onClick}
            onKeyDown={onKeyDown}
            role="button"
            tabIndex={0}
        >
            {tooltip && isLoading ? (
                <div className={css(searchInputWrapperStyle)}>
                    <Tooltip content={tooltip}>
                        <span>
                            <Spinner />
                            <input
                                className={css(searchInputStyle)}
                                onChange={onInputChange}
                                placeholder={placeholder}
                                data-testid={dataTestId}
                                value={value}
                                disabled={disabled}
                                ref={(node) => (inputRef.current = node ?? null)}
                                onFocus={onFocus}
                                onBlur={onBlur}
                            />
                        </span>
                    </Tooltip>
                </div>
            ) : (
                <>
                    {isLoading ? <Spinner /> : <SearchIcon />}
                    <input
                        className={css(searchInputStyle)}
                        onChange={onInputChange}
                        placeholder={placeholder}
                        data-testid={dataTestId}
                        value={value}
                        disabled={disabled}
                        ref={(node) => (inputRef.current = node ?? null)}
                        onFocus={onFocus}
                        onBlur={onBlur}
                    />
                </>
            )}
        </div>
    );
});

export { SearchBar };
